import AppAuth from 'AppAuth';
import { LaunchDarklyFlags } from 'appTypes';
import { moduleConfig, ZYW_LEADS_RETRY_FLAG } from 'lib/config/appConstants';
/*
 * retries a function a number of times at a specified interval
 */
export function retry<RETURN_TYPE>(
  fn: () => Promise<RETURN_TYPE>,
  retriesLeft = 3, // default to 3 times
  interval = 1000 // once per second
) {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            localStorage.setItem(ZYW_LEADS_RETRY_FLAG, 'false');
            reject(error);
            return;
          }

          // local storage flag not set or false, reload the page
          if (
            !localStorage.getItem(ZYW_LEADS_RETRY_FLAG) ||
            localStorage.getItem(ZYW_LEADS_RETRY_FLAG) !== 'true'
          ) {
            localStorage.setItem(ZYW_LEADS_RETRY_FLAG, 'true');
            window.location.reload();
          }

          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
}

/*
 * returns boolean of status of Google javascript being loaded
 */
export const readyGoogleMap = () => (window.google ? true : false);

/*
 * return title of the app
 */
export const getAppTitle = ({ module }: { module?: 'EB' | 'PC' }) => {
  let appTitle = 'Zywave';
  if (module === 'EB' || module === 'PC') {
    appTitle = moduleConfig[module].title;
  }

  return appTitle;
};

/**
 * Returns the module name based on the provided URL.
 * If the URL contains "/eb", it returns the EB module name.
 * If the URL contains "/pc", it returns the PC module name.
 * Otherwise, it returns an empty string.
 *
 * @param url The URL to check for the module name.
 */
export const getModuleFromUrl = (url: string): string => {
  if (url.toLowerCase().includes('/eb')) {
    return moduleConfig.EB.module;
  }
  if (url.toLowerCase().includes('/pc')) {
    return moduleConfig.PC.module;
  }
  if (url.toLowerCase().includes('/edge/results/dashboard/')) {
    return moduleConfig.EB.module;
  }
  return '';
};

/**
 * Check to hide or not hide zui navigation, it returns true or false.
 * - module exist return false
 * - module does not exist:
 *    + check url contains one of array return true,
 *    + other return false
 */
export const hideZuiNavigation = (module: string): boolean => {
  if (module) {
    return false;
  }

  const urls_hide_zui_navigation = [
    '/dot-details/',
    '/osha-details/',
    '/osha-details2/',
    '/whd-details/',
  ];

  const pathname = window.location.pathname.toLowerCase();

  return urls_hide_zui_navigation.some((v) => pathname.includes(v));
};

/**
 * converts a variable to an integer value
 */
export function intVal(n: number | string): number {
  return typeof n === 'number' ? n : parseInt(n, 10);
}

export function isAllowedBrokerSearch(flags?: LaunchDarklyFlags): boolean {
  // AppAuth.userHasBrokerSearchEb is defined in AppAuth.js (non-typed)
  // We need to cast to maintain type safety while interfacing with JS
  return AppAuth.userHasBrokerSearchEb(
    flags as Parameters<typeof AppAuth.userHasBrokerSearchEb>[0]
  );
}

export const isZywaveUrl = (): boolean => {
  const ZUI_URI = 'zywave.com';
  return window.location.href.toLowerCase().indexOf(ZUI_URI) > 0;
};
